import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';
import { roleColors, type UserRole } from '../utils/roleColors';
import {
  Folder,
  FileBox,
  History,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  Upload,
  Download,
  ClipboardList,
} from 'lucide-react';
import { format } from 'date-fns';

// Role-specific greeting messages
const roleGreetings: Record<UserRole, { title: string; subtitle: string }> = {
  LawEnforcement: {
    title: 'Evidence Portal',
    subtitle: 'Upload and track evidence for your cases',
  },
  Attorney: {
    title: 'Discovery Portal',
    subtitle: 'Access case evidence and discovery materials',
  },
  PublicSubmitter: {
    title: 'Evidence Submission',
    subtitle: 'Submit evidence for court review',
  },
  CaseManager: {
    title: 'Case Management',
    subtitle: 'Review submissions and manage evidence',
  },
  SystemAdmin: {
    title: 'Administration',
    subtitle: 'Monitor system activity and audit trails',
  },
  Judge: {
    title: 'Courtroom Portal',
    subtitle: 'Review case evidence and manage court proceedings',
  },
};

// Quick actions per role
const roleActions: Record<UserRole, Array<{ label: string; href: string; icon: typeof Upload }>> = {
  LawEnforcement: [
    { label: 'Upload Evidence', href: '/evidence/upload', icon: Upload },
    { label: 'My Cases', href: '/cases', icon: Folder },
  ],
  Attorney: [
    { label: 'Case Evidence', href: '/evidence', icon: FileBox },
    { label: 'Download Report', href: '/audit', icon: Download },
  ],
  PublicSubmitter: [
    { label: 'My Cases', href: '/my-cases', icon: Folder },
    { label: 'Submit Evidence', href: '/submit', icon: Upload },
  ],
  CaseManager: [
    { label: 'Pending Submissions', href: '/submissions', icon: ClipboardList },
    { label: 'All Cases', href: '/cases', icon: Folder },
  ],
  SystemAdmin: [
    { label: 'Audit Log', href: '/audit', icon: History },
    { label: 'All Evidence', href: '/evidence', icon: FileBox },
  ],
  Judge: [
    { label: 'Courtroom', href: '/courtroom', icon: ClipboardList },
    { label: 'My Cases', href: '/cases', icon: Folder },
  ],
};

export default function Dashboard() {
  const { user, hasRole } = useAuth();
  const userRole = (user?.role || 'PublicSubmitter') as UserRole;
  const colors = roleColors[userRole];
  const greeting = roleGreetings[userRole];
  const actions = roleActions[userRole];

  const { data: cases, isLoading: casesLoading } = useQuery({
    queryKey: ['cases'],
    queryFn: () => apiClient.getCases(),
  });

  const { data: custodyStats, isLoading: statsLoading } = useQuery({
    queryKey: ['custody-stats'],
    queryFn: () => apiClient.getCustodyStats(),
  });

  // Filter cases to only those the user is assigned to (for LawEnforcement/Attorney/Judge)
  const assignedCases = cases?.filter(c => {
    if (c.caseNumber === 'PUBLIC-SUBMISSION') return false;
    if (!hasRole(['LawEnforcement', 'Attorney', 'Judge'])) return true;
    const emailLower = (user?.email || '').toLowerCase();
    const nameLower = (user?.name || '').toLowerCase();
    // Check by email (for demo users) or name (for display names)
    return !!(
      c.assignedTo?.toLowerCase().includes(emailLower) ||
      c.leadInvestigator?.toLowerCase().includes(emailLower) ||
      c.prosecutingAttorney?.toLowerCase().includes(emailLower) ||
      c.defenseAttorney?.toLowerCase().includes(emailLower) ||
      c.assignedJudge?.toLowerCase().includes(emailLower) ||
      c.assignedTo?.toLowerCase().includes(nameLower) ||
      c.leadInvestigator?.toLowerCase().includes(nameLower) ||
      c.prosecutingAttorney?.toLowerCase().includes(nameLower) ||
      c.defenseAttorney?.toLowerCase().includes(nameLower) ||
      c.assignedJudge?.toLowerCase().includes(nameLower)
    );
  }) ?? [];

  // Fetch evidence across assigned cases for LawEnforcement/Attorney to compute personal stats
  const { data: assignedCaseEvidence } = useQuery({
    queryKey: ['assigned-evidence', assignedCases.map(c => c.caseNumber).join(','), user?.email],
    queryFn: async () => {
      const results = await Promise.all(
        assignedCases.map(c => apiClient.getCaseEvidence(c.caseNumber).catch(() => []))
      );
      return results.flat();
    },
    enabled: hasRole(['LawEnforcement', 'Attorney', 'Judge']) && assignedCases.length > 0,
  });

  const { data: myEvidence } = useQuery({
    queryKey: ['my-evidence'],
    queryFn: async () => {
      const caseEvidence = await apiClient.getCaseEvidence('PUBLIC-SUBMISSION');
      return caseEvidence.filter(
        e => e.uploadedBy === user?.email ||
             e.uploadedBy?.toLowerCase().includes((user?.name || '').toLowerCase())
      );
    },
    enabled: userRole === 'PublicSubmitter',
  });

  // Role-specific stats
  const getStatsForRole = () => {
    const baseStats = [
      {
        name: 'Total Cases',
        value: hasRole(['LawEnforcement', 'Attorney'])
          ? assignedCases.length
          : cases?.filter(c => c.caseNumber !== 'PUBLIC-SUBMISSION').length ?? 0,
        icon: Folder,
        color: 'bg-blue-500',
        href: '/cases',
      },
    ];

    if (hasRole(['LawEnforcement', 'Attorney'])) {
      const myUploads = assignedCaseEvidence?.filter(
        e => e.uploadedBy === user?.email ||
             e.uploadedBy?.toLowerCase().includes((user?.name || '').toLowerCase())
      ).length ?? 0;
      return [
        {
          name: 'My Cases',
          value: assignedCases.length,
          icon: Folder,
          color: colors.solid,
          href: '/cases',
        },
        {
          name: 'Total Evidence',
          value: assignedCaseEvidence?.length ?? 0,
          icon: FileBox,
          color: 'bg-green-500',
          href: '/evidence',
        },
        {
          name: 'My Uploads',
          value: myUploads,
          icon: Upload,
          color: 'bg-emerald-500',
        },
        {
          name: 'Verified',
          value: assignedCaseEvidence?.filter(e => e.integrityStatus === 'verified').length ?? 0,
          icon: CheckCircle,
          color: 'bg-teal-500',
        },
      ];
    }

    if (hasRole(['CaseManager', 'SystemAdmin'])) {
      return [
        ...baseStats,
        {
          name: 'Custody Events',
          value: custodyStats?.totalEvents ?? 0,
          icon: History,
          color: 'bg-purple-500',
          href: '/audit',
        },
        {
          name: 'Total Uploads',
          value: custodyStats?.byEventType?.upload ?? 0,
          icon: TrendingUp,
          color: 'bg-amber-500',
        },
        {
          name: 'Integrity Checks',
          value: custodyStats?.byEventType?.integrity_check ?? 0,
          icon: CheckCircle,
          color: 'bg-green-500',
        },
      ];
    }

    if (hasRole(['Judge'])) {
      return [
        {
          name: 'Assigned Cases',
          value: assignedCases.length,
          icon: Folder,
          color: 'bg-slate-600',
          href: '/cases',
        },
        {
          name: 'Total Evidence',
          value: assignedCaseEvidence?.length ?? 0,
          icon: FileBox,
          color: 'bg-slate-500',
          href: '/evidence',
        },
        {
          name: 'Verified Evidence',
          value: assignedCaseEvidence?.filter(e => e.integrityStatus === 'verified').length ?? 0,
          icon: CheckCircle,
          color: 'bg-green-600',
        },
      ];
    }

    // PublicSubmitter
    return [
      {
        name: 'My Submissions',
        value: myEvidence?.length ?? 0,
        icon: FileBox,
        color: colors.solid,
      },
      {
        name: 'Pending Review',
        value: myEvidence?.filter(e => e.evidenceStatus === 'PendingReview').length ?? 0,
        icon: Clock,
        color: 'bg-amber-500',
      },
    ];
  };

  const stats = getStatsForRole();

  return (
    <div className="space-y-10">
      {/* Welcome header with role-specific styling */}
      <div className={`-mx-6 lg:-mx-10 -mt-6 lg:-mt-10 px-6 lg:px-10 py-8 bg-gradient-to-r ${colors.gradient} mb-4`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight">
              Welcome back, {user?.name.split(' ')[0]}
            </h1>
            <p className="text-white/80 mt-1">
              {greeting.subtitle}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {actions.map((action) => (
              <Link
                key={action.href}
                to={action.href}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white font-medium rounded-lg transition-colors"
              >
                <action.icon className="h-5 w-5" />
                {action.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div
            key={stat.name}
            className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6"
          >
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {casesLoading || statsLoading ? '...' : stat.value}
                </p>
              </div>
            </div>
            {'href' in stat && stat.href && (
              <Link
                to={stat.href}
                className="mt-4 block text-sm font-medium text-justice-navy hover:text-justice-gold transition-colors"
              >
                View all →
              </Link>
            )}
          </div>
        ))}
      </div>

      {/* Recent cases - hidden for PublicSubmitter */}
      {userRole !== 'PublicSubmitter' && (
      <div className="bg-white rounded-xl shadow-sm">
        <div className="px-6 py-5 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Recent Cases</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {casesLoading ? (
            <div className="px-6 py-8 text-center text-gray-500">Loading cases...</div>
          ) : cases?.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">No cases found</div>
          ) : (
            cases
              ?.filter(c => c.caseNumber !== 'PUBLIC-SUBMISSION')
              .filter(c => {
                if (!hasRole(['LawEnforcement', 'Attorney'])) return true;
                const nameLower = (user?.name || '').toLowerCase();
                return !!(
                  c.assignedTo?.toLowerCase().includes(nameLower) ||
                  c.leadInvestigator?.toLowerCase().includes(nameLower) ||
                  c.prosecutingAttorney?.toLowerCase().includes(nameLower) ||
                  c.defenseAttorney?.toLowerCase().includes(nameLower) ||
                  c.assignedJudge?.toLowerCase().includes(nameLower)
                );
              })
              .slice(0, 5).map((caseItem) => (
              <Link
                key={caseItem.caseNumber}
                to={`/cases/${caseItem.caseNumber}`}
                className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <Folder className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">{caseItem.caseNumber}</p>
                    <p className="text-sm text-gray-500">{caseItem.title}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      caseItem.status === 'open'
                        ? 'bg-green-100 text-green-800'
                        : caseItem.status === 'pending'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {caseItem.status}
                  </span>
                  {caseItem.evidenceCount !== undefined && (
                    <span className="flex items-center gap-1 text-sm text-gray-500">
                      <FileBox className="h-4 w-4" />
                      {caseItem.evidenceCount}
                    </span>
                  )}
                </div>
              </Link>
            ))
          )}
        </div>
        {cases && cases.length > 5 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <Link to="/cases" className="text-sm text-primary-600 hover:text-primary-700">
              View all {cases.length} cases →
            </Link>
          </div>
        )}
      </div>
      )}

      {/* Recent activity */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {statsLoading ? (
            <div className="px-6 py-8 text-center text-gray-500">Loading activity...</div>
          ) : !custodyStats?.recentActivity?.length ? (
            <div className="px-6 py-8 text-center text-gray-500">No recent activity</div>
          ) : (
            custodyStats.recentActivity.slice(0, 5).map((event) => (
              <div key={event.id} className="flex items-start gap-4 px-6 py-4">
                <div
                  className={`p-2 rounded-full ${
                    event.eventType === 'upload'
                      ? 'bg-green-100'
                      : event.eventType === 'download'
                      ? 'bg-blue-100'
                      : event.eventType === 'integrity_check'
                      ? 'bg-purple-100'
                      : 'bg-gray-100'
                  }`}
                >
                  {event.eventType === 'upload' ? (
                    <FileBox className="h-4 w-4 text-green-600" />
                  ) : event.eventType === 'integrity_check' ? (
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                  ) : event.eventType === 'legal_hold' ? (
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                  ) : (
                    <Clock className="h-4 w-4 text-gray-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">{event.description}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {event.performedByName} • {format(new Date(event.performedAt), 'MMM d, yyyy h:mm a')}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
